import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useMemo, useState } from "react";
import { Waves, Play, ChevronDown, ArrowRight, Search, X } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { MemberUpgradeCard } from "@/components/MemberUpgradeCard";
import { TopicFilter } from "@/components/TopicFilter";
import { matchesTopic, topicList } from "@/lib/topics";
import { FavoriteButton } from "@/components/FavoriteButton";
import { useToolkitPractices } from "@/hooks/useToolkitPractices";
import { PRACTICES, type Practice } from "@/lib/somatic-practices";

const TOPICS = topicList(["grounding", "release", "nervous", "boundaries", "yoga"]);

export const Route = createFileRoute("/somatic")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Somatic Exercises — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Self-guided, illustrated somatic exercises to help settle the nervous system and restore a felt sense of safety. Calm, visual, step-by-step.",
      },
      { property: "og:title", content: "Somatic Exercises — Recovery Trauma™" },
      { property: "og:description", content: "Self-guided, illustrated somatic exercises to help settle the nervous system and restore a felt sense of safety. Calm, visual, step-by-step." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/somatic" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/somatic" }],
  }),
  component: withMemberGate(SomaticPage, "Somatic Exercises"),
});

function PracticeCard({
  practice,
  locked,
}: {
  practice: Practice;
  locked: boolean;
}) {
  return (
    <article className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-3 py-3 sm:px-4">
        <span
          aria-hidden
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--gradient-gold)] text-xl"
        >
          {practice.emoji}
        </span>
        <div className="min-w-0">
          <h3 className="truncate font-display text-[15px] leading-tight text-foreground sm:text-base">
            {practice.title}
          </h3>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {practice.duration} · Self-guided
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <FavoriteButton
            storageKey="rt:fav-somatic"
            id={practice.id}
            label={practice.title}
            sub={practice.duration}
            to="/somatic"
            size="sm"
          />
          {locked ? (
            <span className="inline-flex h-9 items-center gap-1 rounded-full bg-muted px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Member
            </span>
          ) : (
            <Link
              to="/somatic/$practice"
              params={{ practice: practice.id }}
              className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[var(--gradient-gold)] px-3 text-[12px] font-semibold text-foreground shadow-sm transition hover:opacity-90 touch-manipulation"
            >
              <Play className="h-3.5 w-3.5" /> Begin
            </Link>
          )}
        </div>
      </div>

      <p className="px-4 pb-4 text-[12px] leading-relaxed text-muted-foreground">
        {practice.blurb}
      </p>
    </article>
  );
}

function YogaLinkCard({ practice }: { practice: Practice }) {
  return (
    <Link
      to="/yoga"
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 overflow-hidden rounded-2xl border border-border/60 bg-card px-3 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-[var(--shadow-gold)] sm:px-4"
    >
      <span
        aria-hidden
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--gradient-gold)] text-xl"
      >
        {practice.emoji}
      </span>
      <div className="min-w-0">
        <h3 className="truncate font-display text-[15px] leading-tight text-foreground sm:text-base">
          {practice.title}
        </h3>
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
          {practice.duration} · Full studio
        </p>
      </div>
      <span className="inline-flex h-9 items-center gap-1 rounded-full bg-[var(--gradient-gold)] px-3 text-[12px] font-semibold text-foreground shadow-sm">
        Open <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}

function SomaticPage() {
  const { isMember } = useMembership();
  const [topic, setTopic] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const { data: dbItems } = useToolkitPractices();

  const practices = useMemo(() => {
    const q = query.trim().toLowerCase();
    const staticIds = new Set(PRACTICES.map((p) => p.id));
    const extra = (dbItems ?? []).filter((p) => p.kind === "somatic" && !staticIds.has(p.id));
    return [...PRACTICES, ...extra].filter(
      (p) =>
        matchesTopic(p, topic) &&
        (!q ||
          p.title.toLowerCase().includes(q) ||
          p.blurb.toLowerCase().includes(q)),
    );
  }, [topic, query, dbItems]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <header className="mb-6 text-center sm:mb-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground">
          <Waves className="h-3.5 w-3.5" /> Somatic Exercises
        </div>
        <h1 className="font-display text-2xl tracking-tight text-foreground sm:text-4xl md:text-5xl">
          Somatic Exercises
        </h1>
        <p className="mx-auto mt-2 hidden max-w-xl text-base leading-relaxed text-muted-foreground sm:block">
          Calm, illustrated body-based practices to settle the nervous system.
          Each exercise opens on its own page — like a beautifully illustrated guide.
        </p>
      </header>

      <details className="group mb-5 rounded-2xl border border-lavender/30 bg-lavender/10 px-4 py-2 text-sm text-foreground">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-[12px] font-medium">
          <span className="inline-flex items-center gap-2">
            <span aria-hidden>🌿</span> A gentle note before you begin
          </span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
        </summary>
        <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
          Body-based work can stir sensation. If you feel flooded, pause, place a hand on your heart,
          and orient to the room. Less is more — choice is always yours.
        </p>
      </details>

      <Link
        to="/nervous-system-reset"
        className="group mb-6 flex items-center gap-4 rounded-2xl border border-[var(--gold-deep)]/35 bg-[var(--gradient-gold)] p-4 shadow-sm transition-all hover:shadow-md"
      >
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-background/70 text-xl">
          🌿
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-widest text-foreground/70">
            Guided programme
          </p>
          <p className="font-display text-[15px] text-foreground">
            Reset &amp; Regulate
          </p>
          <p className="text-[12px] leading-snug text-foreground/80">
            Notice, move, discover what helps — and build your own toolkit.
          </p>
        </div>
        <ArrowRight className="h-4 w-4 text-foreground/60 transition-transform group-hover:translate-x-0.5" />
      </Link>

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search exercises — breathing, shaking, hug…"
          aria-label="Search somatic exercises"
          className="w-full rounded-full border border-border/60 bg-card py-2.5 ps-9 pe-9 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-[var(--gold-deep)] focus:ring-2 focus:ring-[var(--gold-deep)]/20"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute end-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <TopicFilter heading="Browse by Topic" topics={TOPICS} value={topic} onChange={setTopic} />

      {/* Quick shortcut, tucked next to the filter */}
      <Link
        to="/quick-resets"
        className="group -mt-2 mb-5 flex items-center gap-2.5 rounded-full border border-gold/30 bg-[var(--gradient-gold)]/20 px-3.5 py-2 text-[12px] transition hover:border-gold/60"
      >
        <span aria-hidden className="text-sm">⚡</span>
        <span className="flex-1 truncate text-foreground">
          <span className="font-semibold">Need to settle in 60 seconds?</span>
          <span className="hidden text-muted-foreground sm:inline"> — Quick Nervous System Resets</span>
        </span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)] transition group-hover:translate-x-0.5" />
      </Link>


      <div className="space-y-2.5">
        {practices.length === 0 && (
          <p className="rounded-2xl border border-border/50 bg-card px-4 py-8 text-center text-sm text-muted-foreground">
            Nothing matches that search — try a different word, or clear the topic filter.
          </p>
        )}
        {practices.map((p) =>
          p.id === "trauma-informed-yoga" ? (
            <YogaLinkCard key={p.id} practice={p} />
          ) : (
            <PracticeCard
              key={p.id}
              practice={p}
              locked={p.tier === "member" && !isMember}
            />
          ),
        )}
      </div>

      {!isMember && (
        <div className="mt-10">
          <MemberUpgradeCard
            title="Unlock the full body-based library"
            body="Members get every practice — including TRE, pendulation, inner container, full yoga studio, and new releases each month."
          />
        </div>
      )}
    </div>
  );
}
